import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { IconLoader2 } from '@tabler/icons-react';
import { cn } from '@/libs/utils';
import { buttonVariants } from './button-variants';

interface ButtonPropsBase extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'default' | 'secondary' | 'tertiary' | 'destructive' | 'link' | 'outline' | 'ghost' | 'success' | 'bagde';
  size?: 'default' | 'md' | 'sm' | 'badge';
  iconOnly?: boolean; // New prop for icon-only buttons
}

type ButtonProps = ButtonPropsBase &
  (
    | { asChild: true }
    | {
        asChild?: false;
        loading?: boolean;
        leftSection?: React.ReactNode;
        rightSection?: React.ReactNode;
        icon?: React.ReactNode; // New prop for icon-only buttons
      }
  );

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, iconOnly, children, ...props }, ref) => {
    const { asChild, ...rest } = props;

    // Handle the asChild case first
    if (asChild) {
      return (
        <Slot className={cn(buttonVariants({ variant, size, iconOnly, className }))} ref={ref} {...rest}>
          {children}
        </Slot>
      );
    }

    const { loading = false, leftSection, rightSection, icon, disabled, ...otherProps } = props;

    // Automatically set iconOnly to true if icon is provided
    const isIconOnly = iconOnly || Boolean(icon && !children);

    return (
      <button
        className={cn(buttonVariants({ variant, size, iconOnly: isIconOnly, className }))}
        disabled={loading || disabled}
        ref={ref}
        {...otherProps}
      >
        {/* When there's only an icon */}
        {isIconOnly && !loading && (icon || children)}

        {/* When it's a loading icon-only button */}
        {isIconOnly && loading && <IconLoader2 className="w-5 h-5 animate-spin" />}

        {/* Standard button with content - only render if not icon-only */}
        {!isIconOnly && (
          <>
            {((leftSection && loading) || (!leftSection && !rightSection && loading)) && (
              <IconLoader2 className="mr-2 w-4 h-4 animate-spin" />
            )}
            {!loading && leftSection && <div className="mr-2">{leftSection}</div>}
            {children}
            {!loading && rightSection && <div className="ml-2">{rightSection}</div>}
            {rightSection && loading && <IconLoader2 className="ml-2 w-4 h-4 animate-spin" />}
          </>
        )}
      </button>
    );
  }
);
Button.displayName = 'Button';

// Icon-only button convenience component
const IconButton = React.forwardRef<
  HTMLButtonElement,
  Omit<ButtonProps, 'iconOnly' | 'leftSection' | 'rightSection' | 'asChild'> & {
    'aria-label': string; // Make aria-label required for accessibility
  }
>(({ className, children, ...props }, ref) => {
  return (
    <Button iconOnly ref={ref} className={className} {...props}>
      {children}
    </Button>
  );
});
IconButton.displayName = 'IconButton';

export { Button, IconButton };
